import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save, Lock, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ChangePasswordTab({ getAuthHeaders }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Toate câmpurile sunt obligatorii');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Parola nouă trebuie să aibă cel puțin 6 caractere');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Parolele noi nu se potrivesc');
      return;
    }
    
    if (currentPassword === newPassword) {
      toast.error('Parola nouă trebuie să fie diferită de cea curentă');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        getAuthHeaders()
      );
      
      toast.success('Parolă schimbată cu succes!');
      
      // Reset form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Parola curentă este incorectă');
      } else {
        toast.error(error.response?.data?.detail || 'Eroare la schimbarea parolei');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Lock className="w-5 h-5" />
          Schimbă Parola
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Actualizați parola pentru contul de administrator
        </p>
      </div>

      <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
        {/* Current Password */}
        <div>
          <Label htmlFor="current_password">Parola Curentă</Label>
          <div className="relative">
            <Input
              id="current_password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Introduceți parola curentă"
              data-testid="current-password-input"
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div>
          <Label htmlFor="new_password">Parolă Nouă</Label>
          <div className="relative">
            <Input
              id="new_password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Introduceți parola nouă (minim 6 caractere)"
              data-testid="new-password-input"
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Minim 6 caractere
          </p>
        </div>

        {/* Confirm Password */}
        <div>
          <Label htmlFor="confirm_password">Confirmă Parola Nouă</Label>
          <div className="relative">
            <Input
              id="confirm_password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Reintroduceți parola nouă"
              data-testid="confirm-password-input"
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <Button
            type="submit"
            disabled={loading}
            data-testid="change-password-button"
            style={{ backgroundColor: '#1a73e8' }}
          >
            {loading ? (
              <>
                <Lock className="mr-2 h-4 w-4 animate-pulse" />
                Se salvează...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Schimbă Parola
              </>
            )}
          </Button>
        </div>

        {/* Security Tips */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-md">
          <h3 className="font-semibold text-amber-900 mb-2">💡 Sfaturi de Securitate</h3>
          <ul className="text-sm text-amber-800 space-y-1">
            <li>• Folosiți o parolă cu cel puțin 8 caractere</li>
            <li>• Combinați litere mari, mici, cifre și caractere speciale</li>
            <li>• Nu folosiți aceeași parolă ca la alte conturi</li>
            <li>• Schimbați parola periodic pentru securitate maximă</li>
          </ul>
        </div>
      </form>
    </div>
  );
}

export default ChangePasswordTab;
